export type ReconciliationStatusValue = 'needs_attention' | 'reconciled'

export type DurationDiscrepancyApi =
  | 'LESS_THAN_5_MINUTES'
  | 'BETWEEN_5_AND_15_MINUTES'
  | 'BETWEEN_15_AND_30_MINUTES'
  | 'MORE_THAN_30_MINUTES'

export interface ReconciliationWarnings {
  clockInMismatch: number
  clockInEarly: number
  clockInLate: number
  clockOutEarly: number
  clockOutLate: number
  skillMismatch: boolean
  genderMismatch: boolean
  clockInLocationMismatch?: boolean
  clockOutLocationMismatch?: boolean
}

export interface ReconciliationApiItem {
  _id: string
  subject?: { id: string; name: string; picture: string }
  instanceType: { id: string; name: string }
  teamMember: {
    id: string
    name: string
    picture: string
  }
  plannedTime?: {
    start: string
    end?: string
    duration?: number
  } | null
  actualTime: {
    start: string
    end: string | null
    duration: number | null
  } | null
  warnings: ReconciliationWarnings | null
  warningsCount: number | null
  status: ReconciliationStatusValue
  instanceStatus: string
  durationDiscrepancy: number | null
  paidTime: number | null
  payRate: string | null
  payRateType: string | null
  paymentCardName: string | null
  isCustomInstancePayRate?: boolean | null
  payAmount: string | null
  dontPayReason: string | null
  payBy: string | null
  cancelledBy: string | null
  cancelledReason: string | null
  cancelledFinancialImpact: boolean | null
  cancelledPreventable: boolean | null
  cancelledNotes: string | null
  timezone?: string | null
}

export interface ReconciliationListApiResponse {
  data: ReconciliationApiItem[]
  pagination: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
  }
}

export interface ReconciliationRowTime {
  startTime: string
  endTime: string
  durationMinutes: number
}

export interface ReconciliationRowActualTime {
  startTime: string
  endTime: string | null
  durationMinutes: number | null
}

export interface ReconciliationRow {
  rotaInstanceId: string
  startTimeIso: string
  teamMemberId: string
  status: ReconciliationStatusValue
  date: string
  teamMember: { id: string; name: string; avatarUrl: string | null }
  serviceUser: { id: string; name: string; avatarUrl: string | null } | null
  eventType: { id: string; name: string }
  plannedTime: ReconciliationRowTime
  actualTime: ReconciliationRowActualTime | null
  isCanceled: boolean
  durationDiscrepancy: number | null
  warningCount: number
  rawWarnings: ReconciliationWarnings | null
  payRate: string | null
  payRateType: string | null
  paymentCardName: string | null
  isCustomInstancePayRate: boolean | null
  paidTime: number | null
  payAmount: string | null
  hasShiftRate: boolean
  dontPayReason: string | null
  payBy: string | null
  cancelledBy: string | null
  cancelledReason: string | null
  cancelledFinancialImpact: boolean | null
  cancelledPreventable: boolean | null
  cancelledNotes: string | null
  timezone: string | null
}

export type ReconciliationSortField =
  | 'startTime'
  | 'status'
  | 'teamMemberName'
  | 'subjectName'
  | 'instanceTypeName'
  | 'plannedDuration'
  | 'durationDiscrepancy'
  | 'warnings'
  | 'paidTime'
  | 'payRate'
  | 'payAmount'
  | 'actualTimeDuration'
  | 'plannedTimeDuration'

export interface ReconciliationFilters {
  startTime: string
  endTime: string
  status?: ReconciliationStatusValue
  teamMemberIds?: string[]
  serviceUserIds?: string[]
  eventTypeIds?: string[]
  durationDiscrepancy?: DurationDiscrepancyApi[]
  sortField?: ReconciliationSortField
  sortDir?: 1 | -1
  page?: number
  pageSize?: number
}

export enum ReconciliationPayBy {
  PLANNED_TIME = 'planned_time',
  ACTUAL_TIME = 'actual_time',
  DONT_PAY = 'dont_pay',
  CUSTOM_PAID_TIME = 'custom_paid_time',
  CUSTOM_AMOUNT = 'custom_amount'
}

export interface UpdateReconciliationPayload {
  teamMemberId: string
  reconciliationStatus: ReconciliationStatusValue
  payBy?: ReconciliationPayBy
  customPaidTimeStart?: string
  customPaidTimeEnd?: string
  customAmountToPay?: string
  dontPayReason?: string
}

export interface BulkUpdatePayByPayload {
  paymentIds: [string, string][]
  payBy: ReconciliationPayBy.PLANNED_TIME | ReconciliationPayBy.ACTUAL_TIME
}
